import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { SOURCING_API_URL, setCollectorMode } from './api';

// Talks to the "Sourcing Go&Dev" extension through its content script (crm-bridge.js).
// With the extension, the user logs in on the real linkedin.com and LinkedIn pages are
// collected in this browser: the LinkedIn session never leaves Chrome.

let counter = 0;

function ask(type, payload = {}, timeoutMs = 4000) {
  return new Promise((resolve, reject) => {
    const id = `src-${Date.now()}-${counter += 1}`;
    let timer;
    const onMessage = (event) => {
      const msg = event.data;
      if (event.source !== window || !msg || msg.source !== 'sourcing-extension' || msg.id !== id) return;
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      if (msg.payload?.error) reject(new Error(msg.payload.error));
      else resolve(msg.payload);
    };
    timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('extension-absente'));
    }, timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage({ source: 'sourcing-crm', id, type, payload }, window.location.origin);
  });
}

export const EXTENSION_FOLDER = 'sourcing_frontend/extension';

/** Opens the real linkedin.com login page in a new tab. */
export const openRealLinkedin = () =>
  ask('login').catch(() => window.open('https://www.linkedin.com/login', '_blank', 'noopener,noreferrer'));

const KEEPALIVE_MS = 20000;

/**
 * Extension + LinkedIn status of this browser. While the Sourcing page is open and LinkedIn is
 * connected, keeps the in-browser collector running and switches imports to it.
 */
export function useBrowserLinkedin() {
  const [state, setState] = useState({ checking: true, installed: false, connected: false, account: null });

  const refresh = useCallback(async () => {
    try {
      const res = await ask('status', {}, 1500);
      setState({ checking: false, installed: true, connected: !!res.linkedin?.connected, account: res.linkedin?.account || null });
    } catch (e) {
      setState({ checking: false, installed: false, connected: false, account: null });
    }
  }, []);

  useEffect(() => {
    refresh();
    // Back from the linkedin.com tab: re-check the account.
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const ready = state.installed && state.connected;
  useEffect(() => {
    setCollectorMode(ready ? 'browser' : 'playwright');
    if (!ready) return undefined;
    const keepAlive = () => ask('collector', { apiBase: SOURCING_API_URL }).catch(() => {});
    keepAlive();
    const id = setInterval(keepAlive, KEEPALIVE_MS);
    return () => {
      clearInterval(id);
      setCollectorMode('playwright');
    };
  }, [ready]);

  return { ...state, ready, refresh };
}

export const LinkedinContext = createContext(null);
export const useLinkedin = () => useContext(LinkedinContext);
